<!--
  CollectorPhoto — one photo tile of the collector's line card and detail rail
  (collector-app P1-D, handoff spec Section 2.5/2.6). A lilac placeholder with the
  picture over it, an optional tag along the bottom edge («صورة العميلة»,
  «بوست»), and a «+N» face when the tile stands for the photos behind it.

  `loading="lazy"` and `crossorigin="anonymous"` are the spec's: phase 2's
  service-worker caching needs the CORS request so the responses are readable
  (not opaque). A picture that fails to load leaves the placeholder.

  `framed` is HER photo's purple ring (collector-clarity, rule G). It is a
  box-shadow, so it sits OUTSIDE the tile: the parent keeps 3 px free on every
  side or the ring is cut.
-->
<template>
  <span
    class="c-photo"
    :class="[
      `c-photo--${size}`,
      { 'c-photo--more': more != null, 'c-photo--current': current, 'c-photo--framed': framed },
    ]"
  >
    <span v-if="more != null" class="c-photo__more c-num"><bdi>+{{ more }}</bdi></span>
    <template v-else>
      <img
        v-if="src && !failed"
        :src="src"
        :alt="alt"
        loading="lazy"
        crossorigin="anonymous"
        draggable="false"
        @error="failed = true"
      />
      <span v-if="tag" class="c-photo__tag">{{ tag }}</span>
    </template>
  </span>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  /** The thumbnail url (`thumbUrl`, else the original). */
  src: { type: String, default: null },
  alt: { type: String, default: '' },
  /** The short label along the bottom edge. */
  tag: { type: String, default: null },
  /** `xl` 120, `lg` 92, `hero` 84, `her` 76, `md` 64, `rail` 56, `hq` 52, `sm` 44, `xs` 28 — px, set by the class. */
  size: { type: String, default: 'md' },
  /** Her photo's purple ring. */
  framed: { type: Boolean, default: false },
  /** When a number, the tile is the «+N» face instead of a picture. */
  more: { type: Number, default: null },
  /** The thumb rail's selected tile. */
  current: { type: Boolean, default: false },
});

const failed = ref(false);
watch(() => props.src, () => { failed.value = false; });
</script>

<style scoped>
.c-photo {
  display: block;
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
  background: var(--c-lilac);
}

.c-photo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.c-photo--xl { width: 120px; height: 120px; border-radius: 14px; }
.c-photo--lg { width: 92px; height: 92px; border-radius: 12px; }
.c-photo--her { width: 76px; height: 76px; border-radius: 12px; }
.c-photo--md { width: 64px; height: 64px; border-radius: 12px; }
.c-photo--hq { width: 52px; height: 52px; border-radius: 10px; }
.c-photo--sm { width: 44px; height: 44px; border-radius: 10px; }
.c-photo--xs { width: 28px; height: 28px; border-radius: 6px; }
.c-photo--rail { width: 56px; height: 56px; border-radius: 10px; }
.c-photo--hero { width: 84px; height: 84px; border-radius: 12px; }

.c-photo--framed { box-shadow: 0 0 0 3px var(--c-purple); }

.c-photo--current {
  outline: 2px solid var(--c-indigo);
  outline-offset: 1px;
}

.c-photo--more {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--c-border);
  color: var(--c-indigo);
  font-size: 14px;
  font-weight: 700;
}

.c-photo--xs.c-photo--more { font-size: 10px; }

.c-photo__tag {
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  padding: 3px;
  background: rgba(49, 0, 111, 0.85);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  text-align: center;
  line-height: 1.2;
}

.c-photo--sm .c-photo__tag,
.c-photo--hq .c-photo__tag { padding: 2px; font-size: 9px; }
</style>
